/**
 * Utility for parsing tagged text with <highlight> segments.
 */

export interface ParsedTaggedTextTextPart {
    type: "text";
    text: string;
}

export interface ParsedTaggedTextHighlightPart {
    type: "highlight";
    text: string;
    highlightIndex: number;
}

export type ParsedTaggedTextPart = ParsedTaggedTextTextPart | ParsedTaggedTextHighlightPart;

export interface ParsedTaggedText {
    parts: ParsedTaggedTextPart[];
    highlights: string[];
    collapsedText: string;
    collapsedPreviewText: string;
    plainText: string;
}

/**
 * Returns true when a parsed part is a highlight segment.
 */
function isHighlightPart(part: ParsedTaggedTextPart | undefined): part is ParsedTaggedTextHighlightPart {
    return part?.type === "highlight";
}

/**
 * Normalizes omitted-boundary text into either an omission marker or a plain separator.
 */
function getCollapsedBoundaryKind(text: string): "marker" | "separator" {
    return text.trim().length > 0 ? "marker" : "separator";
}

/**
 * Builds the collapsed semantic text from the parsed parts without inventing adjacency separators.
 */
function buildCollapsedText(parts: ParsedTaggedTextPart[]): string {
    const collapsedChunks: string[] = [];

    parts.forEach((part, index) => {
        if (part.type === "highlight") {
            collapsedChunks.push(part.text);
            return;
        }

        const previousPart = parts[index - 1];
        const nextPart = parts[index + 1];
        if (!isHighlightPart(previousPart) || !isHighlightPart(nextPart)) {
            return;
        }

        collapsedChunks.push(" ");
    });

    return collapsedChunks.join("");
}

/**
 * Builds the collapsed preview text that visually marks only omitted context boundaries.
 */
function buildCollapsedPreviewText(parts: ParsedTaggedTextPart[]): string {
    const collapsedChunks: string[] = [];

    parts.forEach((part, index) => {
        if (part.type === "highlight") {
            collapsedChunks.push(part.text);
            return;
        }

        const previousPart = parts[index - 1];
        const nextPart = parts[index + 1];
        const hasPreviousHighlight = isHighlightPart(previousPart);
        const hasNextHighlight = isHighlightPart(nextPart);

        if (!hasPreviousHighlight && !hasNextHighlight) {
            return;
        }

        const boundaryKind = getCollapsedBoundaryKind(part.text);
        if (boundaryKind === "separator") {
            if (hasPreviousHighlight && hasNextHighlight) {
                collapsedChunks.push(" ");
            }
            return;
        }

        if (!hasPreviousHighlight && hasNextHighlight) {
            collapsedChunks.push("[...] ");
            return;
        }

        if (hasPreviousHighlight && !hasNextHighlight) {
            collapsedChunks.push(" [...]");
            return;
        }

        collapsedChunks.push(" [...] ");
    });

    return collapsedChunks.join("");
}

/**
 * Parses a string containing one or more <highlight>...</highlight> segments.
 * Throws an error if the tags are missing, nested, duplicated in the wrong order, or malformed.
 */
export function parseTaggedText(text: string): ParsedTaggedText {
    const startTag = '<highlight>';
    const endTag = '</highlight>';
    const tagPattern = /<\/?highlight>/g;
    const parts: ParsedTaggedTextPart[] = [];
    const highlights: string[] = [];

    if (!text.includes(startTag)) {
        throw new Error("Missing <highlight> tag");
    }
    if (!text.includes(endTag)) {
        throw new Error("Missing </highlight> tag");
    }

    // Tighten validation: no other < or > tags allowed
    const sanitized = text.replace(/<highlight>/g, '').replace(/<\/highlight>/g, '');
    if (sanitized.includes('<') || sanitized.includes('>')) {
        throw new Error("Forbidden markup: only <highlight> tags are allowed");
    }

    let insideHighlight = false;
    let cursor = 0;
    let tagMatch: RegExpExecArray | null = null;

    while ((tagMatch = tagPattern.exec(text)) !== null) {
        const matchedTag = tagMatch[0];
        const tagIndex = tagMatch.index;

        if (matchedTag === startTag) {
            if (insideHighlight) {
                throw new Error("Nested <highlight> segments found");
            }

            const textPart = text.slice(cursor, tagIndex);
            if (textPart) {
                parts.push({ type: "text", text: textPart });
            }

            insideHighlight = true;
            cursor = tagIndex + startTag.length;
            continue;
        }

        if (!insideHighlight) {
            throw new Error("Malformed <highlight> tags order");
        }

        const highlightText = text.slice(cursor, tagIndex);
        const highlightIndex = highlights.length;
        highlights.push(highlightText);
        parts.push({ type: "highlight", text: highlightText, highlightIndex });
        insideHighlight = false;
        cursor = tagIndex + endTag.length;
    }

    if (insideHighlight) {
        throw new Error("Missing </highlight> tag");
    }

    const trailingText = text.slice(cursor);
    if (trailingText) {
        parts.push({ type: "text", text: trailingText });
    }

    const plainText = parts.map((part) => part.text).join("");
    const collapsedText = buildCollapsedText(parts);

    return {
        parts,
        highlights,
        collapsedText,
        collapsedPreviewText: buildCollapsedPreviewText(parts),
        plainText
    };
}

/**
 * Checks if a string contains one or more valid <highlight> segments.
 */
export function hasHighlightSegments(text: string): boolean {
    try {
        parseTaggedText(text);
        return true;
    } catch {
        return false;
    }
}
